/**
 * エンドツーエンドパイプラインのオーケストレーター。
 *
 * 入力: files, metadata, heroIndex
 * 出力: ZIP Blob (80枚の画像 + captions.csv)
 *
 * 5 ステップ:
 * 1. classify 全件 (並列 10) — カテゴリ + hasReflection + confidence
 * 2. 非ヒーローをブラウザ WebWorker プールで画像処理 (並列 hardwareConcurrency)
 * 3. 非ヒーローの外装カテゴリ (2-5) を /api/remove-reflection で
 *    「福山工業所」文字だけ除去 (並列 3、失敗時は元画像にフォールバック)。
 *    Wave 11: Claude の hasReflection ゲート廃止。外装なら全件 Gemini に送る。
 * 4. ヒーローを Gemini API で合成 (失敗時はフォールバック)
 * 5. CaptionEntry 生成 + ZIP 梱包
 *
 * テスト用に外部依存を DI 可能にする。デフォルトはブラウザの実装。
 */

import type { ClassificationResult, VehicleMetadata } from '@shared/types';

import { composeHeroForFile, type HeroResult } from '../api/hero-client';
import { buildCaptionsCsvBlob, type CaptionEntry, generateCaptions } from '../caption';
import type { ProcessedImage, Result } from '../image';
import {
  type CleanupBlobFn,
  defaultClassifyAll,
  defaultCleanupBlob,
  defaultProcessNonHero,
} from './browser-deps';
import { runCleanupStage } from './cleanup-stage';
import type { PipelineProgress, PipelineResult, ProgressCallback } from './types';
import { assembleOutputs, buildZipBlob, zipNameFor } from './zip-builder';

export type { CleanupBlobFn } from './browser-deps';

// ===== DI 型 ============================================================

export interface PipelineDeps {
  readonly classifyAll?: (files: readonly File[]) => Promise<readonly ClassificationResult[]>;
  readonly composeHero?: (file: File) => Promise<HeroResult>;
  readonly processNonHero?: (
    files: readonly File[],
    onProgress: (completed: number) => void,
    /** Wave 19: classifications を渡して smart crop の bbox を Worker に配管 */
    classifications?: readonly ClassificationResult[],
  ) => Promise<ReadonlyMap<number, Result<ProcessedImage>>>;
  readonly cleanupBlob?: CleanupBlobFn;
  /** cleanup の並列度 (デフォルト 3) */
  readonly cleanupConcurrency?: number;
}

export interface RunPipelineParams {
  readonly files: readonly File[];
  readonly heroIndex: number;
  readonly metadata: VehicleMetadata;
  readonly onProgress?: ProgressCallback;
  readonly deps?: PipelineDeps;
}

// ===== エントリポイント =================================================

export async function runPipeline(params: RunPipelineParams): Promise<PipelineResult> {
  const { files, heroIndex, metadata } = params;
  validateInputs(files, heroIndex);

  const started = performance.now();
  const resolved = resolveDeps(params.deps);
  const report = (progress: PipelineProgress): void => params.onProgress?.(progress);

  const classifications = await runClassify(files, resolved.classifyAll, report);
  const processedByInputIdx = await runProcess(
    files,
    resolved.processNonHero,
    report,
    classifications,
  );
  const cleanupOutcome = await runCleanupStage({
    files,
    heroIndex,
    classifications,
    processed: processedByInputIdx,
    cleanupFn: resolved.cleanupBlob,
    concurrency: resolved.cleanupConcurrency,
    report,
  });
  const hero = await runHero(files, heroIndex, resolved.composeHero, report);

  const entries = generateCaptions({ classifications, heroIndex, metadata });
  const csv = buildCaptionsCsvBlob(
    entries.map((e) => ({ filename: e.filename, caption: e.caption })),
  );
  report({ step: 'building-zip', current: 0, total: entries.length });

  const outputs = buildOutputList(entries, files, heroIndex, hero, cleanupOutcome.map);
  const zipName = zipNameFor(metadata.model, metadata.year);
  const zipBlob = await buildZipBlob(outputs, csv);
  report({ step: 'building-zip', current: entries.length, total: entries.length });

  return {
    zipBlob,
    zipName,
    heroFallbackUsed: hero.fallbackUsed,
    cleanedCount: cleanupOutcome.cleanedCount,
    elapsedMs: Math.round(performance.now() - started),
    totalPhotos: entries.length,
  };
}

// ===== ステージ分割 =====================================================

interface ResolvedDeps {
  readonly classifyAll: (files: readonly File[]) => Promise<readonly ClassificationResult[]>;
  readonly composeHero: (file: File) => Promise<HeroResult>;
  readonly processNonHero: (
    files: readonly File[],
    onProgress: (completed: number) => void,
    classifications?: readonly ClassificationResult[],
  ) => Promise<ReadonlyMap<number, Result<ProcessedImage>>>;
  readonly cleanupBlob: CleanupBlobFn;
  readonly cleanupConcurrency: number;
}

function validateInputs(files: readonly File[], heroIndex: number): void {
  if (files.length === 0) {
    throw new Error('No files to process');
  }
  if (heroIndex < 0 || heroIndex >= files.length) {
    throw new Error(`heroIndex out of range: ${heroIndex}`);
  }
}

function resolveDeps(deps: PipelineDeps | undefined): ResolvedDeps {
  const d: PipelineDeps = deps ?? {};
  return {
    classifyAll: d.classifyAll ?? defaultClassifyAll,
    composeHero: d.composeHero ?? composeHeroForFile,
    processNonHero: d.processNonHero ?? defaultProcessNonHero,
    cleanupBlob: d.cleanupBlob ?? defaultCleanupBlob,
    cleanupConcurrency: d.cleanupConcurrency ?? 3,
  };
}

async function runClassify(
  files: readonly File[],
  fn: ResolvedDeps['classifyAll'],
  report: (p: PipelineProgress) => void,
): Promise<readonly ClassificationResult[]> {
  report({ step: 'classifying', current: 0, total: files.length });
  const result = await fn(files);
  report({ step: 'classifying', current: files.length, total: files.length });
  return result;
}

async function runProcess(
  files: readonly File[],
  fn: ResolvedDeps['processNonHero'],
  report: (p: PipelineProgress) => void,
  classifications?: readonly ClassificationResult[],
): Promise<ReadonlyMap<number, Result<ProcessedImage>>> {
  const nonHeroCount = Math.max(0, files.length - 1);
  report({ step: 'processing-images', current: 0, total: nonHeroCount });
  return fn(
    files,
    (completed) => {
      report({ step: 'processing-images', current: completed, total: nonHeroCount });
    },
    classifications,
  );
}

async function runHero(
  files: readonly File[],
  heroIndex: number,
  fn: ResolvedDeps['composeHero'],
  report: (p: PipelineProgress) => void,
): Promise<HeroResult> {
  report({ step: 'composing-hero', current: 0, total: 1 });
  const heroFile = files[heroIndex];
  if (!heroFile) throw new Error('hero file missing');
  const hero = await fn(heroFile);
  report({ step: 'composing-hero', current: 1, total: 1 });
  return hero;
}

// ===== 出力配列組み立て (CaptionEntry → Blob) ==========================

function buildOutputList(
  entries: readonly CaptionEntry[],
  files: readonly File[],
  heroIndex: number,
  hero: HeroResult,
  processedByInputIdx: ReadonlyMap<number, Result<ProcessedImage>>,
): ReadonlyArray<{ filename: string; blob: Blob }> {
  // entries[0] = hero, entries[1..] = 入力 index 順 (heroIndex 除外)
  const nonHeroInputIndices: number[] = [];
  for (let i = 0; i < files.length; i++) {
    if (i !== heroIndex) nonHeroInputIndices.push(i);
  }
  return assembleOutputs(entries, (entry) => {
    const outIdx = entries.indexOf(entry);
    if (outIdx === 0) return hero.blob;
    const inputIdx = nonHeroInputIndices[outIdx - 1];
    if (inputIdx === undefined) return undefined;
    const r = processedByInputIdx.get(inputIdx);
    if (!r || !r.ok) return undefined;
    return r.value.blob;
  });
}
